<template>
  <div>
    <div class="m-t-5">
      <span class="c-gray p-r-5">{{ $t('Show files with these tags') }}:</span>
      <WdsSelect
        v-model="selectedTags"
        :options="availableTags"
        :placeholder="$t('All tags')"
        multi-select
      >
        <template #select-all-text>
          <WdsText :text="$('Select all')" />
        </template>
      </WdsSelect>
    </div>
    <div :style="height ? { 'max-height': height + 'px' } : {}" class="table-responsive p-t-0">
      <table class="table table-condensed table-striped table-hover">
        <thead v-if="hasFiles || isLoading">
          <tr>
            <th>
              <div class="checkbox m-b-0">
                <label>
                  <input :checked="areAllFilesSelected" type="checkbox" @change="toggleAllFiles" />
                  <i class="input-helper" />
                </label>
              </div>
            </th>
            <th>&nbsp;</th>
            <th v-if="columns.includes('name')" style="width: 99.99%">
              {{ $t('Name') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!hasFiles">
            <tr>
              <template v-if="isLoading">
                <td />
                <td :colspan="columns.length">
                  <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
                  <button-spinner />
                  {{ $t('Loading') }}
                </td>
              </template>
              <template v-else>
                <td :colspan="columns.length">
                  {{
                    excludeIds.length > 0
                      ? $t(
                          'All existing files have been selected. Add new files, by selecting the "Add new file" button above.',
                        )
                      : $t('Click above to add your first file in Worksome.')
                  }}
                </td>
              </template>
            </tr>
          </template>

          <template v-if="hasFiles">
            <tr
              v-for="{ [idProp]: id, name, tags, icon } in filteredFiles"
              :key="id"
              class="pointer"
              @click="toggleSingleFile(id)"
            >
              <td>
                <div class="checkbox m-b-0 m-t-5">
                  <label>
                    <input :checked="selectedFileIds.includes(id)" type="checkbox" />
                    <i class="input-helper" />
                  </label>
                </div>
              </td>
              <td class="p-t-10 p-b-10">
                <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
                <file-icon :icon="icon" size="sm" />
              </td>
              <td v-if="columns.includes('name')">
                <div class="p-t-5">
                  {{ name }}
                  <span v-if="columns.includes('tags')">
                    <span v-for="tag in tags" :key="tag[idProp]" class="label label-default m-l-10">
                      {{ formatTagName(tag.name) }}
                    </span>
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <input
        v-for="localId in selectedFileIds"
        :key="localId"
        :value="localId"
        type="hidden"
        name="files[]"
      />
    </div>
  </div>
</template>

<script>
import { WdsSelect, WdsText } from '@worksome/wds-components'
import { isIterable } from '@/utils/tools'

/**
 * Configuration:
 * Height in pixels - limit the height of the file picker
 * Exclude - array of file ID's to exclude
 */
export default {
  components: { WdsText, WdsSelect },
  props: {
    files: { type: Array, default: () => [] },
    height: { type: Number, default: 0 },
    excludeIds: { type: Array, default: () => [] },
    columns: { type: Array, default: () => ['name', 'tags'] },
    autoSelect: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    useGlobalId: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedTags: [],
      selectedFileIds: [],
    }
  },
  computed: {
    idProp() {
      return this.useGlobalId ? 'globalId' : 'localId'
    },
    availableTags() {
      const allTags = this.files.map(({ tags }) => tags.map((tag) => tag.name)).flat()
      return [...new Set(allTags)].map((name) => ({ value: name, label: name }))
    },
    selectedTagNames() {
      return this.selectedTags.map((tag) => tag.label)
    },
    filteredFiles() {
      if (
        this.selectedTagNames.length === 0 ||
        this.selectedTagNames.length === this.availableTags.length
      ) {
        return this.files
      }
      return this.files.filter(({ [this.idProp]: id, tags }) => {
        const tagNamesOnFile = tags.map((tag) => tag.name)
        return (
          this.autoSelect.includes(id) || // allows recently added files to show
          tagNamesOnFile.some((tag) => this.selectedTagNames.includes(tag))
        )
      })
    },
    selectedFiles() {
      return this.filteredFiles.filter((file) => this.selectedFileIds.includes(file[this.idProp]))
    },
    fileIds() {
      return this.filteredFiles.map((file) => file[this.idProp])
    },
    areAllFilesSelected() {
      const difference = this.selectedFileIds
        .filter((fileId) => !this.fileIds.includes(fileId))
        .concat(this.fileIds.filter((fileId) => !this.selectedFileIds.includes(fileId)))
      return this.hasFiles ? difference.length === 0 : false
    },
    hasFiles() {
      return this.filteredFiles.length > 0
    },
  },
  watch: {
    selectedFiles: {
      handler() {
        this.$emit('selection-updated', this.selectedFiles)
      },
      deep: true,
    },
    autoSelect: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (isIterable(newValue)) {
          this.selectedFileIds = [...new Set([...this.selectedFileIds, ...newValue])]
        }
      },
    },
  },
  methods: {
    formatTagName(tagName) {
      return tagName
        .replaceAll('_', ' ')
        .replace(/^\w/, (char) => char.toUpperCase())
    },

    toggleSingleFile(fileId) {
      this.selectedFileIds = this.selectedFileIds.includes(fileId)
        ? this.selectedFileIds.filter((selectedFileId) => selectedFileId !== fileId)
        : this.selectedFileIds.concat(fileId)
    },
    toggleAllFiles() {
      const difference = this.selectedFileIds
        .filter((fileId) => !this.fileIds.includes(fileId))
        .concat(this.fileIds.filter((fileId) => !this.selectedFileIds.includes(fileId)))
      this.selectedFileIds = difference.length > 0 ? this.fileIds : []
    },
    uncheckAllFiles() {
      this.selectedFileIds = []
    },
  },
}
</script>
